import { ReactNode } from 'react';

export interface BaseChartProps {
  width?: number;
  height?: number;
  topPadding?: number;
  bottomPadding?: number;
  showVerticalGrid?: boolean;
  showHorizontalGrid?: boolean;
  horizontalGridCount?: number;
  children?: ReactNode;
  onMouseMove?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: () => void;
  onTouchStart?: (e: React.TouchEvent<HTMLDivElement>) => void;
  onTouchMove?: (e: React.TouchEvent<HTMLDivElement>) => void;
  onTouchEnd?: () => void;
  overlays?: ReactNode;
  xAxisLabels?: Array<{ index: number; text: string }>;
  yAxisLabels?: Array<{ value: number; text: string }>;
  /** Right-side Y-axis labels for dual-axis charts */
  rightYAxisLabels?: Array<{ value: number; text: string; color?: string }>;
  /** Total number of data points — used to correctly position x-axis labels */
  dataLength?: number;
  className?: string;
}

/**
 * BaseChart - Reusable SVG chart container with grid, axes, and hover detection
 * Provides a foundation for building custom charts with consistent styling
 */
const BaseChart = ({
  width = 1000,
  height = 200,
  topPadding = 20,
  bottomPadding = 20,
  showVerticalGrid = true,
  showHorizontalGrid = true,
  horizontalGridCount = 5,
  children,
  onMouseMove,
  onMouseLeave,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  overlays,
  xAxisLabels = [],
  yAxisLabels = [],
  rightYAxisLabels = [],
  dataLength,
  className = ''
}: BaseChartProps) => {

  // Determine the max data index for positioning x-axis labels
  const maxDataIndex = dataLength 
    ? dataLength - 1 
    : (xAxisLabels.length > 0 ? Math.max(...xAxisLabels.map(l => l.index), 1) : 1);

  // Generate Y-axis positions for horizontal grid lines
  const horizontalGridLines = Array.from({ length: horizontalGridCount }, (_, i) => {
    const position = i / (horizontalGridCount - 1);
    const y = topPadding + (height - bottomPadding - topPadding) * (1 - position);
    return { y, position };
  });

  return (
    <div className={`relative h-48 sm:h-64 w-full pt-2 pb-10 ${className}`}>
      <div className="relative h-full w-full">
        {/* Y-Axis Labels */}
        {yAxisLabels.length > 0 && (
          <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-[10px] text-slate-500 pointer-events-none z-10 -ml-1 pr-2 border-r border-slate-800/30">
            {yAxisLabels.map((label, i) => (
              <span key={i}>{label.text}</span>
            ))}
          </div>
        )}

        {/* Right Y-Axis Labels */}
        {rightYAxisLabels.length > 0 && (
          <div className="absolute right-0 top-0 h-full flex flex-col justify-between text-[10px] pointer-events-none z-10 -mr-1 pl-2 border-l border-slate-800/30">
            {rightYAxisLabels.map((label, i) => (
              <span key={i} style={{ color: label.color || '#94a3b8' }}>{label.text}</span>
            ))}
          </div>
        )}

        <div className={`ml-8 h-full relative ${rightYAxisLabels.length > 0 ? 'mr-8' : ''}`}>
          {/* Hover/touch detection area */}
          {(onMouseMove || onMouseLeave || onTouchStart) && (
            <div 
              className="absolute inset-0 cursor-crosshair z-10"
              style={{ touchAction: 'none' }}
              onMouseMove={onMouseMove}
              onMouseLeave={onMouseLeave}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              onTouchCancel={onTouchEnd}
            />
          )}

          <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full overflow-visible" preserveAspectRatio="none">
            {/* Vertical Grid Lines */}
            {showVerticalGrid && xAxisLabels.map((lbl, i) => (
              <line 
                key={`v-${i}`}
                x1={(lbl.index / maxDataIndex) * width} 
                y1={topPadding} 
                x2={(lbl.index / maxDataIndex) * width} 
                y2={height - bottomPadding} 
                stroke="#334155" 
                strokeWidth="0.5" 
                opacity="0.2"
                vectorEffect="non-scaling-stroke"
              />
            ))}

            {/* Horizontal Grid Lines */}
            {showHorizontalGrid && horizontalGridLines.map((line, i) => (
              <line 
                key={`h-${i}`}
                x1="0" 
                y1={line.y} 
                x2={width} 
                y2={line.y} 
                stroke="#334155" 
                strokeWidth="0.5" 
                opacity={i === 0 || i === horizontalGridCount - 1 ? 0.5 : 0.3}
                strokeDasharray={i === 0 || i === horizontalGridCount - 1 ? "0" : "4 4"}
                vectorEffect="non-scaling-stroke"
              />
            ))}

            {/* Chart content */}
            {children}

            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#38bdf8" />
                <stop offset="100%" stopColor="#38bdf8" stopOpacity="0" />
              </linearGradient>
            </defs>
          </svg>

          {/* Overlays (dots, tooltips, markers, etc.) */}
          {overlays}

          {/* X-Axis Labels */}
          {xAxisLabels.length > 0 && (
            <div className="absolute left-0 bottom-[-24px] w-full flex justify-between text-[10px] text-slate-500">
              {xAxisLabels.map((lbl, idx) => (
                <div 
                  key={idx} 
                  className="absolute transform -translate-x-1/2 whitespace-nowrap"
                  style={{ left: `${(lbl.index / maxDataIndex) * 100}%` }}
                >
                  {lbl.text}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BaseChart;
